import { createApi } from "@reduxjs/toolkit/query/react";
import { components } from "../../types/api";
import { baseQueryWithReauth } from "../../utils/baseQuery";
import { wipApi } from "./wipApi";

// Response types
export type ApiResponseAssociatedCampaignMarginHistory =
  components["schemas"]["ApiResponse_AssociatedCampaignMarginHistory_"];
export type ApiResponseMarginPeriod =
  components["schemas"]["ApiResponse_MarginPeriod_"];
export type ApiResponseMarginPreviewResult =
  components["schemas"]["ApiResponse_MarginPreviewResult_"];
export type ApiResponseBoolean = components["schemas"]["ApiResponse_boolean_"];

// Domain types
export type AssociatedCampaignMarginHistory =
  components["schemas"]["AssociatedCampaignMarginHistory"];
export type CampaignMarginPeriod =
  components["schemas"]["CampaignMarginPeriod"];
export type MarginTimelinePeriod = components["schemas"]["MarginPeriod"];

// Request body types
export type CreateAssociatedCampaignMarginRequest =
  components["schemas"]["CreateAssociatedCampaignMarginBody"];
export type UpdateAssociatedCampaignMarginRequest =
  components["schemas"]["UpdateMarginPeriodInput"];
export type PreviewAssociatedCampaignMarginRequest =
  components["schemas"]["PreviewAssociatedCampaignMarginBody"];
export type AcceptIdealMarginRequest =
  components["schemas"]["AcceptIdealMarginBody"];
export type AcceptIdealMarginResult =
  components["schemas"]["AcceptIdealMarginResult"];
type ApiResponseAcceptIdealMargin =
  components["schemas"]["ApiResponse_AcceptIdealMarginResult_"];
export type AcceptIdealPreview = components["schemas"]["AcceptIdealPreview"];
export type LineItemBudgetChange =
  components["schemas"]["LineItemBudgetChange"];
export type LineItemBudgetTarget =
  components["schemas"]["LineItemBudgetTarget"];
type ApiResponseAcceptIdealPreview =
  components["schemas"]["ApiResponse_AcceptIdealPreview_"];

interface MarginPathParams {
  briefingId: string;
  associatedId: string;
}

const tagId = ({ briefingId, associatedId }: MarginPathParams): string =>
  `${briefingId}:${associatedId}`;

const basePath = ({ briefingId, associatedId }: MarginPathParams): string =>
  `/briefings/${briefingId}/associated-campaigns/${associatedId}/margin`;

// El WIP muestra el margen actualizado de cada línea: cualquier cambio de
// tramo lo deja desfasado, así que se refresca al guardar.
const refreshWip = async (
  _arg: unknown,
  {
    dispatch,
    queryFulfilled,
  }: {
    dispatch: (action: unknown) => unknown;
    queryFulfilled: Promise<unknown>;
  }
): Promise<void> => {
  try {
    await queryFulfilled;
    dispatch(wipApi.util.invalidateTags(["WipMetrics"]));
  } catch {
    // El error ya lo gestiona quien lanza la mutación.
  }
};

export const associatedCampaignMarginApi = createApi({
  reducerPath: "associatedCampaignMarginApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["AssociatedCampaignMargin"],
  endpoints: (builder) => ({
    // GET .../margin — historial consolidado (defaultMargin + timeline + periods)
    getAssociatedCampaignMargin: builder.query<
      ApiResponseAssociatedCampaignMarginHistory,
      MarginPathParams
    >({
      query: (params) => basePath(params),
      providesTags: (_result, _error, params) => [
        { type: "AssociatedCampaignMargin" as const, id: tagId(params) },
      ],
    }),

    // POST .../margin — crear tramo
    createAssociatedCampaignMargin: builder.mutation<
      ApiResponseMarginPeriod,
      { params: MarginPathParams; body: CreateAssociatedCampaignMarginRequest }
    >({
      query: ({ params, body }) => ({
        url: basePath(params),
        method: "POST",
        body,
      }),
      onQueryStarted: refreshWip,
      invalidatesTags: (_result, _error, { params }) => [
        { type: "AssociatedCampaignMargin" as const, id: tagId(params) },
      ],
    }),

    // PUT .../margin/{periodId} — editar tramo
    updateAssociatedCampaignMargin: builder.mutation<
      ApiResponseMarginPeriod,
      {
        params: MarginPathParams;
        periodId: string;
        body: UpdateAssociatedCampaignMarginRequest;
      }
    >({
      query: ({ params, periodId, body }) => ({
        url: `${basePath(params)}/${periodId}`,
        method: "PUT",
        body,
      }),
      onQueryStarted: refreshWip,
      invalidatesTags: (_result, _error, { params }) => [
        { type: "AssociatedCampaignMargin" as const, id: tagId(params) },
      ],
    }),

    // DELETE .../margin/{periodId} — borrar tramo
    deleteAssociatedCampaignMargin: builder.mutation<
      ApiResponseBoolean,
      { params: MarginPathParams; periodId: string }
    >({
      query: ({ params, periodId }) => ({
        url: `${basePath(params)}/${periodId}`,
        method: "DELETE",
      }),
      onQueryStarted: refreshWip,
      invalidatesTags: (_result, _error, { params }) => [
        { type: "AssociatedCampaignMargin" as const, id: tagId(params) },
      ],
    }),

    // POST .../margin/accept-ideal — margen ideal desde mañana (+ DV360 si aplica)
    acceptAssociatedCampaignIdealMargin: builder.mutation<
      ApiResponseAcceptIdealMargin,
      { params: MarginPathParams; body: AcceptIdealMarginRequest }
    >({
      query: ({ params, body }) => ({
        url: `${basePath(params)}/accept-ideal`,
        method: "POST",
        body,
      }),
      onQueryStarted: refreshWip,
      invalidatesTags: (_result, _error, { params }) => [
        { type: "AssociatedCampaignMargin" as const, id: tagId(params) },
      ],
    }),

    // POST .../margin/accept-ideal/preview — qué cambiaría en DV360 al aceptar (IO y line items)
    previewIdealMarginAccept: builder.mutation<
      ApiResponseAcceptIdealPreview,
      { params: MarginPathParams; body: { margin: number } }
    >({
      query: ({ params, body }) => ({
        url: `${basePath(params)}/accept-ideal/preview`,
        method: "POST",
        body,
      }),
    }),

    // POST .../margin/preview — previsualizar el impacto sin guardar
    previewAssociatedCampaignMargin: builder.mutation<
      ApiResponseMarginPreviewResult,
      { params: MarginPathParams; body: PreviewAssociatedCampaignMarginRequest }
    >({
      query: ({ params, body }) => ({
        url: `${basePath(params)}/preview`,
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useGetAssociatedCampaignMarginQuery,
  useCreateAssociatedCampaignMarginMutation,
  useUpdateAssociatedCampaignMarginMutation,
  useDeleteAssociatedCampaignMarginMutation,
  useAcceptAssociatedCampaignIdealMarginMutation,
  usePreviewIdealMarginAcceptMutation,
  usePreviewAssociatedCampaignMarginMutation,
} = associatedCampaignMarginApi;
